import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, User, MapPin, Mail, Trash2, Edit, Shield, Eye, Loader2 } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const EmployeeManagement = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    location_ids: [],
    employee_role: 'mitarbeiter'
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [employeesRes, locationsRes] = await Promise.all([
        axios.get(`${API}/admin/employees`),
        axios.get(`${API}/admin/locations`)
      ]);
      setEmployees(employeesRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e, closeDialog = false) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/admin/employees`, formData);
      toast.success('Mitarbeiter erfolgreich angelegt!');
      
      // Reset form but keep location_ids and employee_role for next employee
      setFormData({
        name: '',
        email: '',
        password: '',
        location_ids: formData.location_ids, // Keep selected locations
        employee_role: formData.employee_role // Keep selected role
      });
      
      // Only close dialog if "Fertig" button was clicked
      if (closeDialog) {
        setDialogOpen(false);
        setFormData({ name: '', email: '', password: '', location_ids: [], employee_role: 'mitarbeiter' });
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Anlegen des Mitarbeiters');
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee({
      ...employee,
      employee_role: employee.employee_role || 'mitarbeiter'
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API}/admin/employees/${editingEmployee.id}`, {
        name: editingEmployee.name,
        email: editingEmployee.email,
        location_ids: editingEmployee.location_ids,
        employee_role: editingEmployee.employee_role
      });
      toast.success('Mitarbeiter aktualisiert');
      setEditDialogOpen(false);
      setEditingEmployee(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'inhaber':
        return <Badge className="bg-purple-600">Inhaber</Badge>;
      case 'stationsleitung':
        return <Badge className="bg-blue-600">Stationsleitung</Badge>;
      default:
        return <Badge variant="secondary">Mitarbeiter</Badge>;
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLocationToggle = (locationId) => {
    setFormData(prev => {
      const currentLocations = prev.location_ids || [];
      if (currentLocations.includes(locationId)) {
        return { ...prev, location_ids: currentLocations.filter(id => id !== locationId) };
      } else {
        return { ...prev, location_ids: [...currentLocations, locationId] };
      }
    });
  };

  const getLocationNames = (locationIds) => {
    if (!locationIds || locationIds.length === 0) return 'Keine Standorte';
    return locations
      .filter(loc => locationIds.includes(loc.id))
      .map(loc => loc.name)
      .join(', ');
  };

  const handleDeleteEmployee = async () => {
    if (!employeeToDelete) return;
    
    try {
      await axios.delete(`${API}/admin/employees/${employeeToDelete.id}`);
      toast.success(`Mitarbeiter ${employeeToDelete.name} wurde gelöscht. Der Superadmin wurde benachrichtigt.`);
      setDeleteDialogOpen(false);
      setEmployeeToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen des Mitarbeiters');
    }
  };

  // Impersonate employee - view their dashboard
  const handleViewDashboard = async (employee) => {
    // WICHTIG: Verhindere verschachtelte Impersonation
    const existingImpersonation = localStorage.getItem('impersonation');
    if (existingImpersonation) {
      toast.error('Bitte beenden Sie zuerst die aktuelle Impersonation, um einen Mitarbeiter anzusehen.');
      return;
    }

    setImpersonating(true);
    try {
      const response = await axios.post(`${API}/api/admin/impersonate/${employee.id}`);
      const { access_token, impersonated_user } = response.data;

      if (!access_token || !impersonated_user) {
        throw new Error('Ungültige Antwort vom Server');
      }

      // Save original admin token AND user data
      const originalToken = localStorage.getItem('token');
      const originalUser = localStorage.getItem('user');

      // Store impersonation data in the SAME format as Superadmin
      const impersonationData = {
        id: impersonated_user.id,
        name: impersonated_user.name,
        email: impersonated_user.email,
        role: impersonated_user.role,
        by: 'admin',  // WICHTIG: Wer impersoniert
        originalToken: originalToken,
        originalUser: originalUser
      };
      
      localStorage.setItem('impersonation', JSON.stringify(impersonationData));
      
      // Also keep the old format for backward compatibility
      localStorage.setItem('impersonated_user', JSON.stringify({
        id: impersonated_user.id,
        name: impersonated_user.name,
        email: impersonated_user.email,
        role: impersonated_user.role,
        employee_role: impersonated_user.employee_role,
        impersonated_by_role: 'admin'
      }));

      // Set new impersonated token
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      toast.success(`Sie sind jetzt als ${impersonated_user.name} angemeldet`, {
        description: 'Klicken Sie auf "Zurück zum Admin" um zurückzukehren'
      });

      // Navigate based on role
      if (impersonated_user.role === 'employee') {
        navigate('/employee');
      } else if (impersonated_user.role === 'stationsleitung') {
        navigate('/admin/dashboard');
      }
      
      // Force page reload to update auth context
      window.location.reload();
    } catch (error) {
      console.error('Impersonation error:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Wechseln zum Mitarbeiter-Dashboard');
    } finally {
      setImpersonating(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="employees-title">
              Mitarbeiterverwaltung
            </h1>
            <p className="text-muted-foreground mt-2">Verwalten Sie Ihre Mitarbeiter und deren Standort-Zuordnungen</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-employee-button">
                <Plus className="mr-2 h-4 w-4" />
                Mitarbeiter hinzufügen
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <form onSubmit={handleSubmit}>
                <DialogHeader>
                  <DialogTitle>Neuen Mitarbeiter anlegen</DialogTitle>
                  <DialogDescription>
                    Erstellen Sie einen neuen Mitarbeiter-Account und weisen Sie Standorte zu.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      placeholder="Max Mustermann"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      required
                      data-testid="employee-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">E-Mail *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="mitarbeiter@email.de"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      required
                      data-testid="employee-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Passwort *</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="4-stelliger PIN (z.B. 1234)"
                      value={formData.password}
                      onChange={(e) => handleChange('password', e.target.value)}
                      required
                      minLength={4}
                      maxLength={20}
                      data-testid="employee-password-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Standorte zuweisen *</Label>
                    <div className="border rounded-md p-3 space-y-2 max-h-40 overflow-y-auto">
                      {locations.length === 0 ? (
                        <p className="text-sm text-muted-foreground">Keine Standorte verfügbar. Bitte erstellen Sie zuerst einen Standort.</p>
                      ) : (
                        locations.map((location) => (
                          <div key={location.id} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`location-${location.id}`}
                              checked={formData.location_ids.includes(location.id)}
                              onChange={() => handleLocationToggle(location.id)}
                              className="h-4 w-4 rounded border-gray-300 dark:border-gray-600"
                              data-testid={`location-checkbox-${location.id}`}
                            />
                            <label
                              htmlFor={`location-${location.id}`}
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                            >
                              {location.name}
                            </label>
                          </div>
                        ))
                      )}
                    </div>
                    {formData.location_ids.length === 0 && (
                      <p className="text-xs text-muted-foreground">Mindestens ein Standort muss zugewiesen werden</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Rolle</Label>
                    <select
                      value={formData.employee_role}
                      onChange={(e) => handleChange('employee_role', e.target.value)}
                      className="w-full h-10 rounded-md border border-input bg-background px-3"
                    >
                      <option value="mitarbeiter">Mitarbeiter</option>
                      <option value="stationsleitung">Stationsleitung</option>
                      <option value="inhaber">Inhaber (max. 1)</option>
                    </select>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setDialogOpen(false);
                      setFormData({ name: '', email: '', password: '', location_ids: [], employee_role: 'mitarbeiter' });
                    }}
                  >
                    Abbrechen
                  </Button>
                  <Button
                    type="button"
                    onClick={(e) => handleSubmit(e, false)}
                    disabled={formData.location_ids.length === 0}
                    data-testid="add-another-employee-button"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Weitere hinzufügen
                  </Button>
                  <Button
                    type="button"
                    onClick={(e) => handleSubmit(e, true)}
                    disabled={formData.location_ids.length === 0}
                    data-testid="submit-employee-button"
                  >
                    Fertig
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {locations.length === 0 && (
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="py-4">
              <p className="text-sm text-yellow-800">
                ⚠️ Sie haben noch keine Standorte angelegt. Bitte erstellen Sie zuerst einen Standort, bevor Sie Mitarbeiter hinzufügen.
              </p>
            </CardContent>
          </Card>
        )}

        <div className="grid gap-4">
          {employees.map((employee) => (
            <Card key={employee.id} data-testid={`employee-card-${employee.id}`}>
              <CardHeader>
                {/* Desktop Layout */}
                <div className="hidden md:flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-primary/10 rounded-md">
                      <User className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-xl">{employee.name}</CardTitle>
                      <CardDescription>
                        <div className="mt-2 space-y-1">
                          <div className="flex items-center gap-2">
                            <Mail className="h-3 w-3" />
                            <span>{employee.email}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <MapPin className="h-3 w-3" />
                            <span>{getLocationNames(employee.location_ids)}</span>
                          </div>
                          <div className="mt-2 flex items-center gap-2">
                            {getRoleBadge(employee.employee_role)}
                            <Badge variant={employee.is_active ? "default" : "secondary"}>
                              {employee.is_active ? 'Aktiv' : 'Inaktiv'}
                            </Badge>
                          </div>
                        </div>
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewDashboard(employee)}
                      disabled={impersonating}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      title="Dashboard ansehen"
                    >
                      {impersonating ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Eye className="h-4 w-4 mr-1" />
                          Dashboard
                        </>
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(employee)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => {
                        setEmployeeToDelete(employee);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Mobile Layout */}
                <div className="md:hidden space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-primary/10 rounded-md flex-shrink-0">
                      <User className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg truncate">{employee.name}</CardTitle>
                      <div className="mt-1 space-y-1">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Mail className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{employee.email}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <MapPin className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{getLocationNames(employee.location_ids)}</span>
                        </div>
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                          {getRoleBadge(employee.employee_role)}
                          <Badge variant={employee.is_active ? "default" : "secondary"} className="text-xs">
                            {employee.is_active ? 'Aktiv' : 'Inaktiv'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Action Buttons - Full Width on Mobile */}
                  <div className="grid grid-cols-3 gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewDashboard(employee)}
                      disabled={impersonating}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 w-full text-xs"
                      title="Dashboard ansehen"
                    >
                      {impersonating ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Eye className="h-4 w-4" />
                          <span className="ml-1">Dashboard</span>
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(employee)}
                      className="w-full"
                    >
                      <Edit className="h-4 w-4" />
                      <span className="ml-1 text-xs">Bearbeiten</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 w-full"
                      onClick={() => {
                        setEmployeeToDelete(employee);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="ml-1 text-xs">Löschen</span>
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))}

          {employees.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <User className="mx-auto h-12 w-12 text-muted-foreground/50" />
                <p className="mt-4 text-muted-foreground">Noch keine Mitarbeiter angelegt</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Klicken Sie auf "Mitarbeiter hinzufügen" um loszulegen
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Mitarbeiter löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Sind Sie sicher, dass Sie den Mitarbeiter "{employeeToDelete?.name}" ({employeeToDelete?.email}) löschen möchten?
                <br /><br />
                <strong>Hinweis:</strong> Der Superadmin wird über diese Löschung benachrichtigt.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => {
                setEmployeeToDelete(null);
              }}>
                Abbrechen
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteEmployee}
                className="bg-red-600 hover:bg-red-700"
              >
                Mitarbeiter löschen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Edit Employee Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Mitarbeiter bearbeiten</DialogTitle>
              <DialogDescription>
                Ändern Sie die Daten des Mitarbeiters.
              </DialogDescription>
            </DialogHeader>
            {editingEmployee && (
              <div className="grid gap-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Name</Label>
                  <Input
                    id="edit-name"
                    value={editingEmployee.name || ''}
                    onChange={(e) => setEditingEmployee({ ...editingEmployee, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-email">E-Mail</Label>
                  <Input
                    id="edit-email"
                    type="email"
                    value={editingEmployee.email || ''}
                    onChange={(e) => setEditingEmployee({ ...editingEmployee, email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Standorte</Label>
                  <div className="border rounded-md p-3 space-y-2 max-h-40 overflow-y-auto">
                    {locations.map((location) => (
                      <div key={location.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`edit-location-${location.id}`}
                          checked={(editingEmployee.location_ids || []).includes(location.id)}
                          onChange={() => {
                            const currentLocations = editingEmployee.location_ids || [];
                            if (currentLocations.includes(location.id)) {
                              setEditingEmployee({
                                ...editingEmployee,
                                location_ids: currentLocations.filter(id => id !== location.id)
                              });
                            } else {
                              setEditingEmployee({
                                ...editingEmployee,
                                location_ids: [...currentLocations, location.id]
                              });
                            }
                          }}
                          className="h-4 w-4 rounded border-gray-300 dark:border-gray-600"
                        />
                        <label htmlFor={`edit-location-${location.id}`} className="text-sm cursor-pointer">
                          {location.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Rolle</Label>
                  <select
                    value={editingEmployee.employee_role || 'mitarbeiter'}
                    onChange={(e) => setEditingEmployee({ ...editingEmployee, employee_role: e.target.value })}
                    className="w-full h-10 rounded-md border border-input bg-background px-3"
                  >
                    <option value="mitarbeiter">Mitarbeiter</option>
                    <option value="stationsleitung">Stationsleitung</option>
                    <option value="inhaber">Inhaber (max. 1)</option>
                  </select>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Abbrechen</Button>
              <Button onClick={handleSaveEdit}>Speichern</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};
